<template>
  <div>
    <h3
      v-if="copyrightId"
      class="mb-3.5 line-clamp-2 text-[18px] leading-6 font-medium text-black"
    >
      {{ details?.name }}
    </h3>

    <div
      v-if="playback"
      class="h-[240px] w-full overflow-hidden rounded-xl bg-[#f6f7f9] p-2"
    >
      <Player
        :type="playerType"
        :url="playback.url"
        :mime-type="playback.mimeType"
        :poster="stablePoster"
        thumbnail-class="size-[152px]"
      />
    </div>
    <div
      v-else
      class="flex h-[168px] w-full items-center justify-center rounded-xl bg-[#f6f7f9] p-2"
    >
      <div class="size-[152px]">
        <Thumbnail :type="fileTypeToThumbnail(type)" :src="previewSrc" />
      </div>
    </div>

    <dl class="mt-3.5" :class="textClass">
      <div :class="rowClass">
        <dt class="max-w-[60%] text-gray-500">Source</dt>
        <dd class="flex flex-col items-end text-right text-gray-800">
          <span class="flex items-center gap-x-1.5">
            <DacrMarkIcon class="size-3" aria-hidden="true" />
            {{ metadata?.sourceLabel ?? 'Dacr' }}
          </span>
          <span v-if="captureType">{{ captureType }}</span>
        </dd>
      </div>

      <div v-if="copyrightId && sourceUrl" :class="rowClass">
        <dt class="max-w-[60%] text-gray-500">Source link</dt>
        <dd class="min-w-0 text-right">
          <button
            type="button"
            class="text-link max-w-full truncate font-normal!"
            @click="emit('viewRegistration', copyrightId)"
          >
            {{ displayUrl(sourceUrl) }}
          </button>
        </dd>
      </div>

      <template v-if="copyrightId">
        <div :class="rowClass">
          <dt class="max-w-[60%] text-gray-500">Owner</dt>
          <dd class="text-right text-gray-800">{{ owner?.name ?? '—' }}</dd>
        </div>
        <div :class="rowClass">
          <dt class="max-w-[60%] text-gray-500">Timestamp</dt>
          <dd v-if="registrations.length" class="flex flex-col items-end gap-1">
            <span
              v-for="registration in shownRegistrations"
              :key="registration.origin.id"
              class="flex items-center gap-x-1.5 text-gray-800"
            >
              <Avatar
                v-if="registration.origin.flag.url"
                :size="12"
                :src="registration.origin.flag.url"
              />
              <DacrMarkIcon v-else class="size-3" aria-hidden="true" />
              {{ formatTimestamp(registration.timestamp) ?? '—' }}
            </span>
            <button
              v-if="registrations.length > 3"
              type="button"
              class="flex cursor-pointer items-center gap-x-1.5 font-medium text-gray-500 hover:text-gray-600"
              @click="expanded = !expanded"
            >
              {{ expanded ? 'Show less' : `+${registrations.length - 3} more` }}
              <ChevronDownIcon
                class="size-3.5 transition-transform"
                :class="expanded && 'rotate-180'"
                aria-hidden="true"
              />
            </button>
          </dd>
          <dd v-else class="text-gray-800">—</dd>
        </div>
      </template>
      <div v-else :class="rowClass">
        <dt class="max-w-[60%] text-gray-500">Date recorded</dt>
        <dd class="text-right text-gray-800">
          {{ (recordedAt && formatTimestamp(recordedAt)) || '—' }}
        </dd>
      </div>

      <div :class="[rowClass, durationSeconds == null && 'border-b-0']">
        <dt class="max-w-[60%] text-gray-500">Type</dt>
        <dd class="text-right text-gray-800">{{ friendlyFileType(type) }}</dd>
      </div>

      <div v-if="durationSeconds != null" :class="[rowClass, 'border-b-0']">
        <dt class="max-w-[60%] text-gray-500">Duration</dt>
        <dd class="text-right text-gray-800">
          {{ formatDuration(durationSeconds) }}
        </dd>
      </div>
    </dl>

    <Button
      v-if="variant === 'sheet' && copyrightId"
      variant="stroke"
      size="lg"
      class="mt-5 w-full"
      @click="emit('viewRegistration', copyrightId)"
    >
      View registration
    </Button>
  </div>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, ref, watch } from 'vue'
import { useQuery } from '@vue/apollo-composable'
import { Avatar, Button, Thumbnail } from '@dacr/library'
import { ChevronDownIcon, DacrMarkIcon } from '@dacr/icons'

import Player from '@/features/copyright/components/Player.vue'
import { FileType as LegacyFileType } from '@/shared/graphql/legacy/generated'
import {
  FileType,
  type InfringingRegistrationFallbackQuery,
} from '@/shared/graphql/router/graphql'
import { formatTimestamp } from '@/shared/utils/datetime'
import { friendlyFileType } from '@/shared/utils/fileHelpers'
import { fileTypeToThumbnail } from '@/shared/utils/thumbnail'
import { applyAuthorizationToken } from '@/shared/utils/url'

import { INFRINGING_REGISTRATION_FALLBACK_QUERY } from './operations'
import type { InfringingFileDetails } from './useInfringingFileDetails'

export interface LocalWork {
  name: string
  type: FileType
  previewUrl?: string
  captureType?: string
  recordedAt?: number
  durationSeconds?: number
  media?: File | null
}

const props = defineProps<{
  variant: 'modal' | 'sheet'
  details: InfringingFileDetails | null
  local: LocalWork
}>()

const emit = defineEmits<{ viewRegistration: [copyrightId: string] }>()

type RegistrationFile = InfringingRegistrationFallbackQuery['file']

const isPlaylist = (url: string | null | undefined) =>
  !!url && /\.m3u8(\?|$)/i.test(url)

const displayUrl = (url: string) => url.replace(/^https?:\/\//, '')

const formatDuration = (seconds: number) => {
  const total = Math.max(0, Math.round(seconds))
  const hours = Math.floor(total / 3600)
  const minutes = Math.floor((total % 3600) / 60)
  const rest = String(total % 60).padStart(2, '0')
  return hours
    ? `${hours}:${String(minutes).padStart(2, '0')}:${rest}`
    : `${minutes}:${rest}`
}

const analysisDuration = (file: RegistrationFile | undefined) => {
  const analysis = file?.analysis
  if (analysis?.__typename === 'VideoAnalysis') return analysis.duration
  if (analysis?.__typename === 'AudioAnalysis') {
    const ends = analysis.tracks.flatMap((track) =>
      track.end == null ? [] : [track.end]
    )
    return ends.length ? Math.max(...ends) : null
  }
  return null
}

const expanded = ref(false)

const textClass = computed(() =>
  props.variant === 'modal'
    ? 'text-[12px] leading-4'
    : 'text-[14px] leading-[22px]'
)

const rowClass =
  'flex items-start justify-between gap-x-3 border-b border-[#e5e5e5] py-1.5'

const metadata = computed(() => props.details?.metadata)
const type = computed(() => props.details?.type ?? props.local.type)
const copyrightId = computed(() => props.details?.copyrightId ?? null)

const indexedPreview = computed(() =>
  isPlaylist(props.details?.asset.previewImageUrl)
    ? undefined
    : (props.details?.asset.previewImageUrl ?? undefined)
)

const { result: registration } = useQuery(
  INFRINGING_REGISTRATION_FALLBACK_QUERY,
  () => ({ id: copyrightId.value ?? '' }),
  () => ({
    clientId: 'router',
    enabled:
      !!copyrightId.value &&
      !(indexedPreview.value && metadata.value?.durationSeconds != null),
  })
)

const previewSrc = computed(() => {
  if (!copyrightId.value && props.local.previewUrl)
    return props.local.previewUrl
  const remote =
    indexedPreview.value || registration.value?.file?.asset?.previewImageUrl
  return remote
    ? applyAuthorizationToken(remote, [{ key: 'width', value: 300 }])
    : props.local.previewUrl
})

const durationSeconds = computed(
  () =>
    metadata.value?.durationSeconds ??
    analysisDuration(registration.value?.file) ??
    props.local.durationSeconds
)

const recordedAt = computed(
  () => metadata.value?.recordedAt ?? props.local.recordedAt
)
const captureType = computed(
  () => metadata.value?.captureType ?? props.local.captureType
)

const current = computed(() => props.details?.currentCopyright)
const owner = computed(() => current.value?.owner ?? metadata.value?.owner)
const sourceUrl = computed(
  () => current.value?.sourceUrl ?? metadata.value?.sourceUrl
)
const registrations = computed(() =>
  current.value
    ? current.value.recognizedOrigins
    : (metadata.value?.recognizedOrigins ?? [])
)
const shownRegistrations = computed(() =>
  expanded.value ? registrations.value : registrations.value.slice(0, 3)
)

const localCaptureUrl = ref<string | null>(null)

watch(
  () => props.local.media,
  (media) => {
    if (localCaptureUrl.value) URL.revokeObjectURL(localCaptureUrl.value)
    localCaptureUrl.value = media ? URL.createObjectURL(media) : null
  },
  { immediate: true }
)

const streamUrl = computed(() => {
  const asset = props.details?.asset
  return asset?.__typename === 'StreamAsset' ? asset.streamUrl : null
})

const isPlayableMedia = (media: File) => /^(audio|video)\//.test(media.type)

const streamMimeType = (url: string) => {
  if (isPlaylist(url)) return undefined
  return (
    new URL(url).searchParams.get('response-content-type') ??
    (/\.webm(\?|$)/i.test(url) ? 'video/webm' : 'video/mp4')
  )
}

const withoutSignature = (url: string) => url.split('?')[0]

const stableStreamUrl = ref<string | null>(null)

watch(
  streamUrl,
  (url) => {
    const current = stableStreamUrl.value
    if (url && current && withoutSignature(url) === withoutSignature(current))
      return
    stableStreamUrl.value = url ?? null
  },
  { immediate: true }
)

const stablePoster = ref<string | undefined>()

watch(
  previewSrc,
  (url) => {
    const current = stablePoster.value
    if (url && current && withoutSignature(url) === withoutSignature(current))
      return
    stablePoster.value = url
  },
  { immediate: true }
)

const playback = computed(() => {
  if (copyrightId.value) return null
  if (type.value !== FileType.Audio && type.value !== FileType.Video)
    return null
  if (stableStreamUrl.value)
    return {
      url: stableStreamUrl.value,
      mimeType: streamMimeType(stableStreamUrl.value),
    }
  const media = props.local.media
  if (localCaptureUrl.value && media && isPlayableMedia(media))
    return { url: localCaptureUrl.value, mimeType: media.type }
  return null
})

const playerType = computed(() =>
  type.value === FileType.Audio ? LegacyFileType.Audio : LegacyFileType.Video
)

onBeforeUnmount(() => {
  if (localCaptureUrl.value) URL.revokeObjectURL(localCaptureUrl.value)
})
</script>
